import { useCallback, useEffect, useState } from "react";
import { canExposePilotSurface } from "@enrein/shared-contracts/pilot-exposure";
import type { DeployEnvironment } from "@enrein/shared-contracts/deploy-env";
import type { StudioApiClient } from "../../../api.js";
import { LaunchProofPanel } from "../../../LaunchProofPanel.js";
import { ReportsPanel } from "../../../ReportsPanel.js";
import { LaunchCertificatePanel } from "../../../LaunchCertificatePanel.js";
import { WebsiteOwnershipRecordPanel } from "../../../WebsiteOwnershipRecordPanel.js";

export function V10ProjectRoomEvidence({
  api,
  projectId,
  deployEnv,
  isExternalProof,
  exportRunId,
}: {
  readonly api: StudioApiClient;
  readonly projectId: string;
  readonly deployEnv: DeployEnvironment;
  readonly isExternalProof: boolean;
  readonly exportRunId: string | null;
}) {
  const exposureInput = {
    deployEnv,
    nodeEnv: import.meta.env.MODE,
    explicitFlags: import.meta.env.VITE_ENREIN_PILOT_FEATURE_FLAGS,
  };
  const showCertificate = canExposePilotSurface("api.launchCertificate", "agency", exposureInput).enabled;
  const showOwnership = canExposePilotSurface("api.websiteOwnershipRecord", "agency", exposureInput).enabled;
  const showSalesRoom =
    isExternalProof && canExposePilotSurface("api.salesRoom", "agency", exposureInput).enabled;

  return (
      <div className="v10-project-evidence-stack" id="v10-project-evidence">
      <div className="v10-scan-embedded-panel" aria-label="Launch proof">
        <LaunchProofPanel api={api} projectId={projectId} />
      </div>
      <div className="v10-scan-embedded-panel" aria-label="Search readiness report">
        <ReportsPanel api={api} projectId={projectId} />
      </div>
      {showCertificate ? (
        <div className="v10-scan-embedded-panel" aria-label="Launch certificate">
          <LaunchCertificatePanel api={api} projectId={projectId} />
        </div>
      ) : null}
      {showOwnership ? (
        <div className="v10-scan-embedded-panel" aria-label="Website ownership">
          <WebsiteOwnershipRecordPanel api={api} projectId={projectId} />
        </div>
      ) : null}
      {showSalesRoom && exportRunId ? (
        <V10SalesRoomSnapshot api={api} projectId={projectId} runId={exportRunId} />
      ) : null}
    </div>
  );
}

function V10SalesRoomSnapshot({
  api,
  projectId,
  runId,
}: {
  readonly api: StudioApiClient;
  readonly projectId: string;
  readonly runId: string;
}) {
  const [headline, setHeadline] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const snapshot = await api.getSalesRoomSnapshot(projectId, runId);
      setHeadline(snapshot.proofStory.summary.headline);
    } catch (caught) {
      setHeadline(null);
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  }, [api, projectId, runId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="v10-scan-embedded-panel" aria-label="Sales room snapshot">
      <h4>Sales room snapshot</h4>
      {headline ? <p>{headline}</p> : null}
      {error ? <p className="v10-muted">{error}</p> : null}
    </div>
  );
}
